import { supabase } from '@/shared/lib/supabase';

/**
 * Admin-side customer operations. These call privileged Edge Functions
 * (customer-import-existing, customer-resend-invite,
 * customer-reset-portal-password) which require staff role.
 *
 * Read-side methods (listAll / getById / getActivity) query Supabase
 * directly. RLS lets staff read all rows; the underlying tables enforce
 * "staff or own customer" policies.
 *
 * Each mutating call carries an Idempotency-Key header so retrying after
 * a network blip doesn't create duplicate state.
 */

export type LifecycleStage =
  | 'lead'
  | 'active'
  | 'suspended'
  | 'cancellation_pending'
  | 'churned';

export interface AdminCustomerSummaryRow {
  customer_id: string;
  email: string | null;
  full_name: string;
  company: string | null;
  lifecycle_stage: LifecycleStage;
  account_manager_id: string | null;
  hosting_count: number;
  domain_count: number;
  open_tickets: number;
  unpaid_total: number;
  last_activity_at: string | null;
  created_at: string;
}

export interface AdminCustomerProfile {
  customer_id: string;
  email: string | null;
  full_name: string;
  phone: string | null;
  company: string | null;
  registration_number: string | null;
  vat_number: string | null;
  billing_address_line1: string | null;
  billing_address_line2: string | null;
  billing_address_city: string | null;
  billing_address_postal_code: string | null;
  billing_address_country: string;
  billing_email: string | null;
  lifecycle_stage: LifecycleStage;
  account_manager_id: string | null;
  /**
   * Open tickets, read from the SAME view the customers LIST reads
   * (`v_admin_customer_summary.open_tickets`) so the two screens cannot
   * disagree. The detail header previously hardcoded `0` here, which meant
   * every customer with open tickets was contradicted by their own list row
   * one click earlier.
   *
   * `null` means the view did not return the column for this caller — render
   * it as `—`, never as `0` (pre-flight checklist: a permission-denied read is
   * not a fact).
   */
  open_tickets: number | null;
  created_at: string;
  updated_at: string;
}

export interface AdminCustomerLink {
  id: string;
  customer_id: string;
  cpanel_username: string;
  whm_server: string;
  primary_domain: string | null;
  service_plan_id: string | null;
  service_plan_name: string | null;
  service_plan_slug: string | null;
  service_plan_recurring_amount: number | null;
  service_plan_billing_cycle: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  next_billing_date: string | null;
  scheduled_termination_at: string | null;
  /** `standard` | `no_cancel` | `no_suspend` — whether billing-cron may suspend
   *  or terminate this account. See domains/hosting/dunningPolicy.ts. */
  dunning_policy: string | null;
  /** Slice 2 (2026-09-10): OBSERVED (WHM) and DESIRED (books) hosting state. */
  hosting_state: string | null;
  hosting_state_observed_at: string | null;
  hosting_desired_state: string | null;
  hosting_desired_reason_code: string | null;
  linked_at: string;
  /** PF-Q3 (2026-06-16). True when an active hosting_subscription exists. */
  recurring_enabled: boolean;
}

export interface AdminAuditEntry {
  id: string;
  customer_id: string | null;
  actor_id: string | null;
  actor_kind: 'customer' | 'staff' | 'system';
  event: string;
  entity_kind: string | null;
  entity_id: string | null;
  before: unknown;
  after: unknown;
  ip: string | null;
  created_at: string;
}

export interface ImportExistingCustomerInput {
  email: string;
  full_name: string;
  company?: string;
  cpanel_username: string;
  whm_server?: string;
  primary_domain: string;
  plan_slug?: string;
  send_invite?: boolean;
}

export interface ImportExistingResponse {
  auth_user_id: string;
  customer_link_id: string;
  invite_sent: boolean;
}

interface ApiError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

/**
 * Error thrown when an Edge Function returns a structured non-2xx response.
 * Callers can branch on `.code` to render targeted UI (e.g. show a warning
 * with a "do this instead" hint for known recoverable cases).
 */
export class EdgeFunctionError extends Error {
  code: string;
  status?: number;
  details?: Record<string, unknown>;
  constructor(code: string, message: string, status?: number, details?: Record<string, unknown>) {
    super(message);
    this.name = 'EdgeFunctionError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

async function invokeEdgeFunction<TRes>(
  name: string,
  body: Record<string, unknown>
): Promise<TRes> {
  const idempotencyKey = crypto.randomUUID();
  const { data, error } = await supabase.functions.invoke<TRes | ApiError>(name, {
    body,
    headers: {
      'Idempotency-Key': idempotencyKey,
    },
  });

  if (error) {
    // supabase-js v2 wraps non-2xx in FunctionsHttpError with `context` being
    // the raw Response. Read the body to surface our structured ApiError.
    const ctx = (error as unknown as { context?: Response }).context;
    if (ctx && typeof ctx.clone === 'function') {
      try {
        const parsed = (await ctx.clone().json()) as ApiError;
        if (parsed.code && parsed.message) {
          throw new EdgeFunctionError(parsed.code, parsed.message, ctx.status, parsed.details);
        }
      } catch (parseErr) {
        if (parseErr instanceof EdgeFunctionError) throw parseErr;
        // Body wasn't JSON or didn't match — fall through.
      }
    }
    throw new Error(error.message);
  }

  // Edge Function returned 2xx — `data` is the response body. Could still be
  // an ApiError shape if the function returned 200 with an error code, which
  // we don't do, but defend defensively.
  if (data && typeof data === 'object' && 'code' in data && 'message' in data) {
    const apiErr = data as ApiError;
    throw new EdgeFunctionError(apiErr.code, apiErr.message, 200, apiErr.details);
  }

  return data as TRes;
}

type SummaryRow = {
  customer_id: string;
  email: string | null;
  full_name: string;
  company: string | null;
  lifecycle_stage: LifecycleStage;
  account_manager_id: string | null;
  hosting_count: number | null;
  domain_count: number | null;
  open_tickets: number | null;
  unpaid_total: number | string | null;
  last_activity_at: string | null;
  created_at: string;
};

const num = (v: number | string | null | undefined): number => {
  if (v === null || v === undefined) return 0;
  if (typeof v === 'number') return v;
  return Number(v) || 0;
};

const SUMMARY_COLS =
  'customer_id, email, full_name, company, lifecycle_stage, account_manager_id, hosting_count, domain_count, open_tickets, unpaid_total, last_activity_at, created_at';

const PROFILE_COLS =
  'id, full_name, phone, company, registration_number, vat_number, billing_address_line1, billing_address_line2, billing_address_city, billing_address_postal_code, billing_address_country, billing_email, lifecycle_stage, account_manager_id, created_at, updated_at';

const LINK_SELECT =
  'id, customer_id, cpanel_username, whm_server, primary_domain, service_plan_id, current_period_start, current_period_end, next_billing_date, scheduled_termination_at, dunning_policy, hosting_state, hosting_state_observed_at, hosting_desired_state, hosting_desired_reason_code, linked_at, recurring_enabled, service_plan:service_plan_id(id, name, slug, recurring_amount, billing_cycle)';

interface ProfileRow {
  id: string;
  full_name: string;
  phone: string | null;
  company: string | null;
  registration_number: string | null;
  vat_number: string | null;
  billing_address_line1: string | null;
  billing_address_line2: string | null;
  billing_address_city: string | null;
  billing_address_postal_code: string | null;
  billing_address_country: string;
  billing_email: string | null;
  lifecycle_stage: LifecycleStage;
  account_manager_id: string | null;
  created_at: string;
  updated_at: string;
}

interface LinkRow {
  id: string;
  customer_id: string;
  cpanel_username: string;
  whm_server: string;
  primary_domain: string | null;
  service_plan_id: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  next_billing_date: string | null;
  scheduled_termination_at: string | null;
  dunning_policy: string | null;
  hosting_state: string | null;
  hosting_state_observed_at: string | null;
  hosting_desired_state: string | null;
  hosting_desired_reason_code: string | null;
  linked_at: string;
  recurring_enabled: boolean;
  service_plan:
    | {
        id: string;
        name: string;
        slug: string;
        recurring_amount: number | string;
        billing_cycle: string;
      }
    | null;
}

interface AuditRow {
  id: string;
  customer_id: string | null;
  actor_id: string | null;
  actor_kind: 'customer' | 'staff' | 'system';
  event: string;
  entity_kind: string | null;
  entity_id: string | null;
  before: unknown;
  after: unknown;
  ip: string | null;
  created_at: string;
}

export const adminCustomerService = {
  async importExisting(input: ImportExistingCustomerInput): Promise<ImportExistingResponse> {
    return invokeEdgeFunction<ImportExistingResponse>(
      'customer-import-existing',
      input as unknown as Record<string, unknown>
    );
  },

  /**
   * Read all customers for the admin list, backed by `v_admin_customer_summary`.
   * Staff-only via RLS on the underlying tables.
   */
  async listAll(): Promise<AdminCustomerSummaryRow[]> {
    const { data, error } = await supabase
      .from('v_admin_customer_summary')
      .select(SUMMARY_COLS)
      .order('created_at', { ascending: false });

    if (error) {
      throw new Error(`Could not load customers: ${error.message}`);
    }
    const rows = (data ?? []) as SummaryRow[];
    return rows.map((r) => ({
      customer_id: r.customer_id,
      email: r.email,
      full_name: r.full_name,
      company: r.company,
      lifecycle_stage: r.lifecycle_stage,
      account_manager_id: r.account_manager_id,
      hosting_count: r.hosting_count ?? 0,
      domain_count: r.domain_count ?? 0,
      open_tickets: r.open_tickets ?? 0,
      unpaid_total: num(r.unpaid_total),
      last_activity_at: r.last_activity_at,
      created_at: r.created_at,
    }));
  },

  /**
   * Read a single customer's profile + email. Returns null if not found.
   */
  async getById(customerId: string): Promise<AdminCustomerProfile | null> {
    const [profileRes, summaryRes] = await Promise.all([
      supabase
        .from('customer_profile')
        .select(PROFILE_COLS)
        .eq('id', customerId)
        .maybeSingle(),
      supabase
        .from('v_admin_customer_summary')
        .select('email, open_tickets')
        .eq('customer_id', customerId)
        .maybeSingle(),
    ]);

    if (profileRes.error) {
      throw new Error(`Could not load customer: ${profileRes.error.message}`);
    }
    if (!profileRes.data) return null;

    const row = profileRes.data as ProfileRow;
    const summary = summaryRes.data as
      | { email: string | null; open_tickets: number | null }
      | null;
    const email = summary?.email ? summary.email : null;
    // `?? null`, not `?? 0` — see the AdminCustomerProfile field comment.
    const openTickets = summary?.open_tickets ?? null;

    return {
      customer_id: row.id,
      email,
      open_tickets: openTickets,
      full_name: row.full_name,
      phone: row.phone,
      company: row.company,
      registration_number: row.registration_number,
      vat_number: row.vat_number,
      billing_address_line1: row.billing_address_line1,
      billing_address_line2: row.billing_address_line2,
      billing_address_city: row.billing_address_city,
      billing_address_postal_code: row.billing_address_postal_code,
      billing_address_country: row.billing_address_country,
      billing_email: row.billing_email,
      lifecycle_stage: row.lifecycle_stage,
      account_manager_id: row.account_manager_id,
      created_at: row.created_at,
      updated_at: row.updated_at,
    };
  },

  /**
   * List active customer_link rows for a given customer with their plan name
   * + monthly amount. Used by the Hosting tab on Customer Detail.
   */
  async listLinks(customerId: string): Promise<AdminCustomerLink[]> {
    const { data, error } = await supabase
      .from('customer_link')
      .select(LINK_SELECT)
      .eq('customer_id', customerId)
      .is('unlinked_at', null)
      .order('linked_at', { ascending: false });

    if (error) {
      throw new Error(`Could not load hosting links: ${error.message}`);
    }
    const rows = (data ?? []) as unknown as LinkRow[];
    return rows.map((r) => ({
      id: r.id,
      customer_id: r.customer_id,
      cpanel_username: r.cpanel_username,
      whm_server: r.whm_server,
      primary_domain: r.primary_domain,
      service_plan_id: r.service_plan_id,
      service_plan_name: r.service_plan?.name ?? null,
      service_plan_slug: r.service_plan?.slug ?? null,
      service_plan_recurring_amount: r.service_plan
        ? num(r.service_plan.recurring_amount)
        : null,
      service_plan_billing_cycle: r.service_plan?.billing_cycle ?? null,
      current_period_start: r.current_period_start,
      current_period_end: r.current_period_end,
      next_billing_date: r.next_billing_date,
      scheduled_termination_at: r.scheduled_termination_at,
      dunning_policy: r.dunning_policy,
      hosting_state: r.hosting_state ?? null,
      hosting_state_observed_at: r.hosting_state_observed_at ?? null,
      hosting_desired_state: r.hosting_desired_state ?? null,
      hosting_desired_reason_code: r.hosting_desired_reason_code ?? null,
      linked_at: r.linked_at,
      recurring_enabled: r.recurring_enabled ?? false,
    }));
  },

  /**
   * Reverse-chrono audit log entries for a single customer (capped).
   */
  async getActivity(
    customerId: string,
    opts?: { limit?: number; since?: string }
  ): Promise<AdminAuditEntry[]> {
    const limit = opts?.limit ?? 100;
    let q = supabase
      .from('audit_log')
      .select(
        'id, customer_id, actor_id, actor_kind, event, entity_kind, entity_id, before, after, ip, created_at'
      )
      .eq('customer_id', customerId)
      .order('created_at', { ascending: false })
      .limit(limit);
    if (opts?.since) {
      q = q.gte('created_at', opts.since);
    }
    const { data, error } = await q;
    if (error) {
      throw new Error(`Could not load activity: ${error.message}`);
    }
    return ((data ?? []) as AuditRow[]).map((r) => ({
      id: r.id,
      customer_id: r.customer_id,
      actor_id: r.actor_id,
      actor_kind: r.actor_kind,
      event: r.event,
      entity_kind: r.entity_kind,
      entity_id: r.entity_id,
      before: r.before,
      after: r.after,
      ip: r.ip,
      created_at: r.created_at,
    }));
  },

  async resendInvite(customerId: string): Promise<{ ok: true; invite_sent: boolean }> {
    return invokeEdgeFunction('customer-resend-invite', { customer_id: customerId });
  },

  async resetPortalPassword(
    customerId: string
  ): Promise<{ ok: true; reset_sent: boolean }> {
    return invokeEdgeFunction('customer-reset-portal-password', {
      customer_id: customerId,
    });
  },
};
